/**
 * Creator Studio's Resend-style primitives (FOR-286). Values are copied from
 * the shared reference (justingluska/resend-styles, patterns/01–12): interactive
 * fill gray-a2 with gray-a3 hover, status tone = a3 fill + a11 text, 32px
 * controls at 12px radius, 24px cards with no fill, 28px page titles.
 *
 * Studio-local on purpose: the log-explorer and ui/* primitives are shared by
 * other pages, so they are overridden here at the call site instead.
 */
import { forwardRef, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { Check, ChevronDown, CircleHelp, Search } from "lucide-react"
import { Button, type ButtonProps } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { TONE, rs, tabClass, type Tone } from "./ui-classes"

/* ------------------------------------------------------------------------ */
/* Class recipes                                                             */
/* ------------------------------------------------------------------------ */

/* ------------------------------------------------------------------------ */
/* Badge                                                                     */
/* ------------------------------------------------------------------------ */

export function Badge({ tone, children, className, title }: { tone: Tone; children: ReactNode; className?: string; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1 truncate whitespace-nowrap rounded-lg border border-transparent px-2 text-xs font-medium",
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* Buttons                                                                   */
/* ------------------------------------------------------------------------ */

const BUTTON: Record<"primary" | "secondary" | "ghost" | "small", string> = {
  primary:
    "h-8 gap-1 rounded-xl border border-black bg-black px-3 text-sm font-semibold text-white shadow-none hover:bg-black/80 hover:text-white dark:border-white dark:bg-white dark:text-black dark:hover:bg-[#f0f0f0] dark:hover:text-black",
  secondary:
    "h-8 gap-1 rounded-xl border border-black/[0.15] bg-black/[0.08] px-3 text-sm font-normal text-[#333] shadow-none hover:bg-black/[0.15] hover:text-[#191919] dark:border-[#b0c7d925] dark:bg-[#18191ce0] dark:text-[#a1a4a5] dark:hover:bg-[#b0c7d925] dark:hover:text-white",
  ghost:
    "h-8 gap-1 rounded-xl border border-transparent bg-transparent px-3 text-sm font-normal text-[#333] shadow-none hover:bg-black/[0.08] hover:text-[#191919] dark:text-[#a1a4a5] dark:hover:bg-[#18191ce0] dark:hover:text-white",
  small:
    "h-6 gap-1 rounded-lg border border-transparent bg-transparent px-2 text-xs font-normal text-[#333] shadow-none hover:bg-black/[0.15] hover:text-[#191919] dark:text-[#a1a4a5] dark:hover:bg-[#b0c7d925] dark:hover:text-white [&_svg]:size-3",
}

export type SButtonKind = keyof typeof BUTTON

/** Button in one of the reference's four looks. Disabled = 70% opacity. */
export const SButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant" | "size"> & { kind?: SButtonKind; icon?: boolean }>(
  function SButton({ kind = "secondary", icon, className, ...props }, ref) {
    return (
      <Button
        ref={ref}
        variant="ghost"
        className={cn(
          BUTTON[kind],
          "transition duration-200 ease-in-out disabled:opacity-70",
          icon && (kind === "small" ? "w-6 min-w-6 px-0" : "w-8 min-w-8 px-0 [&_svg]:size-[18px]"),
          className,
        )}
        {...props}
      />
    )
  },
)

/* ------------------------------------------------------------------------ */
/* Layout                                                                    */
/* ------------------------------------------------------------------------ */

/** Page header band: one 28px title, at most one short line under it. */
export function PageHeader({
  overline,
  title,
  subtitle,
  actions,
  className,
}: {
  overline?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex flex-col gap-4 md:flex-row md:items-center md:justify-between", className)}>
      <div className="min-w-0">
        {overline && <div className={cn("text-sm font-semibold", rs.text)}>{overline}</div>}
        <h1 className={cn(rs.h1, "truncate")}>{title}</h1>
        {subtitle && <div className="mt-1 truncate text-sm text-muted-foreground">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** A question-mark that holds the one sentence of guidance a section needs. */
export function Hint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label="More about this" className={cn("inline-flex shrink-0 items-center text-muted-foreground hover:text-foreground", className)}>
          <CircleHelp className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className={rs.tooltip}>{children}</TooltipContent>
    </Tooltip>
  )
}

/** Card (07): 24px radius, hairline border, no fill. Title is a short noun. */
export function Card({
  title,
  hint,
  meta,
  actions,
  children,
  className,
  bodyClassName,
  flush,
}: {
  title?: ReactNode
  hint?: ReactNode
  /** Right of the title, muted: a count, a timezone. */
  meta?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  /** No body padding: for tables and lists that run edge to edge. */
  flush?: boolean
}) {
  return (
    <section className={cn(rs.card, "min-w-0", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4 md:px-6 md:pt-5">
          <div className="flex min-w-0 items-center gap-2">
            {title && <h2 className={rs.subheading}>{title}</h2>}
            {hint && <Hint>{hint}</Hint>}
            {meta && <span className="text-sm text-muted-foreground">{meta}</span>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn(flush ? "pt-3" : "p-4 md:p-6", (title || actions) && !flush && "pt-4 md:pt-4", bodyClassName)}>{children}</div>
    </section>
  )
}

/** Small uppercase label over a value (07 meta grid). */
export function Meta({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <span className={rs.label}>{label}</span>
      <div className="min-w-0 text-sm text-foreground">{children}</div>
    </div>
  )
}

/** Stat (07): label above a 35px number. A link when `to` is given. */
export function Stat({ label, value, to, dot }: { label: ReactNode; value: ReactNode; to?: string; dot?: string }) {
  const body = (
    <>
      <span className={cn(rs.label, "flex items-center gap-1.5")}>
        {dot && <span className={cn("h-2 w-2 rounded-full", dot)} />}
        {label}
      </span>
      <span className={rs.stat}>{value}</span>
    </>
  )
  return to ? (
    <Link to={to} className="group flex flex-col gap-1 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-black/[0.15] [&:hover_span:last-child]:underline [&:hover_span:last-child]:decoration-1 [&:hover_span:last-child]:underline-offset-4">
      {body}
    </Link>
  ) : (
    <div className="flex flex-col gap-1">{body}</div>
  )
}

/* ------------------------------------------------------------------------ */
/* Tabs, segments, filters                                                   */
/* ------------------------------------------------------------------------ */

export function Segments<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  label: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-nowrap items-center gap-1">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} onClick={() => onChange(o.value)} className={tabClass(o.value === value)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Filter select (05 secondary/filter button): `value === ""` is "any". */
export function FilterSelect({
  anyLabel,
  value,
  options,
  onChange,
  className,
}: {
  anyLabel: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  className?: string
}) {
  const current = options.find((o) => o.value === value)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex h-8 min-w-0 items-center justify-between gap-1 rounded-xl pl-3 pr-2 text-sm font-normal transition duration-200 ease-in-out aria-expanded:bg-black/[0.15] dark:aria-expanded:bg-[#b0c7d925]",
            rs.interactive,
            rs.interactiveHover,
            current ? "text-foreground" : rs.text,
            className,
          )}
        >
          <span className="truncate">{current?.label ?? anyLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-aria-expanded:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn(rs.panel, "max-h-96 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto")}>
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {[{ value: "", label: anyLabel }, ...options].map((o) => (
            <DropdownMenuRadioItem key={`opt:${o.value}`} value={o.value} className="mx-1 rounded-xl px-3 py-2 pl-8 text-sm">
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Search input (05): icon on the left, committed on Enter or blur. */
export function SearchInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  className,
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  className?: string
}) {
  return (
    <div className={cn("relative w-full", className)}>
      <Search className={cn("pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2", rs.text)} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            onSubmit()
          }
        }}
        onBlur={onSubmit}
        placeholder={placeholder}
        className={cn("h-8 w-full appearance-none border pl-8 pr-3 outline-none transition duration-200 ease-in-out", rs.input)}
      />
    </div>
  )
}

/** A check that reads like a toggle chip (used for platform pickers). */
export function ToggleChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} className={cn(tabClass(on), "gap-1.5 font-normal", on && "font-semibold")}>
      {on && <Check className="h-3.5 w-3.5" />}
      {children}
    </button>
  )
}
